import React, { useState, useEffect, useRef } from 'react';
import Layout from '@/components/Layout';
import { useAuth } from '@/contexts/AuthContext';
import axios from '@/utils/axiosConfig';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Users, UserPlus, Trash2, Edit, Eye, UserCheck } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const ClientGroups = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [groups, setGroups] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [formData, setFormData] = useState({
    group_name: '',
    group_type: 'famille',
    principal_client_id: '',
    client_ids: [],
    description: ''
  });

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const API = `${BACKEND_URL}/api`;

  // Protection contre les mises à jour sur composant démonté
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => { isMountedRef.current = false; };
  }, []);

  useEffect(() => {
    fetchGroups();
    fetchClients();
  }, []);

  const fetchGroups = async () => {
    try {
      if (isMountedRef.current) setLoading(true);
      const response = await axios.get(`${API}/client-groups`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (isMountedRef.current) {
        // Filter out groups with invalid data
        const validGroups = (response.data || []).filter(g => 
          g && g.id && g.group_name && g.clients && g.clients.length > 0
        );
        
        setGroups(validGroups);
      }
    } catch (error) {
      console.error('Error fetching groups:', error);
      if (isMountedRef.current) {
        if (error.response?.status === 500) {
          toast.error('Erreur serveur lors du chargement des groupes');
        } else if (error.response?.status === 403) {
          toast.error('Vous n\'avez pas la permission de voir les groupes');
        } else {
          toast.error('Erreur lors du chargement des groupes');
        }
        setGroups([]);
      }
    } finally {
      if (isMountedRef.current) setLoading(false);
    }
  };

  const fetchClients = async () => {
    try {
      const response = await axios.get(`${API}/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (isMountedRef.current) {
        // Filter only clients that are not in a group
        const allUsers = response.data;
        const clientUsers = allUsers.filter(u => u.role === 'client' && !u.group_id);
        setClients(clientUsers);
      }
    } catch (error) {
      console.error('Error fetching clients:', error);
      if (isMountedRef.current) toast.error('Erreur lors du chargement des clients');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.group_name || !formData.principal_client_id || formData.client_ids.length === 0) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }
    
    if (formData.client_ids.length < 2) {
      toast.error('Un groupe doit contenir au moins 2 clients');
      return;
    }
    
    if (!formData.client_ids.includes(formData.principal_client_id)) {
      toast.error('Le client principal doit faire partie du groupe');
      return;
    }

    try {
      await axios.post(`${API}/client-groups`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Groupe créé avec succès');
      setDialogOpen(false);
      resetForm();
      fetchGroups();
      fetchClients();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Erreur lors de la création du groupe';
      toast.error(errorMsg);
      console.error('Error creating group:', error);
    }
  };

  const handleDeleteGroup = async (groupId, groupName) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer le groupe "${groupName}" ?`)) {
      return;
    }

    try {
      await axios.delete(`${API}/client-groups/${groupId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Groupe supprimé avec succès');
      fetchGroups();
      fetchClients();
    } catch (error) {
      toast.error('Erreur lors de la suppression du groupe');
    }
  };

  const handleClientSelection = (clientId) => {
    const isSelected = formData.client_ids.includes(clientId);
    
    if (isSelected) {
      setFormData({
        ...formData,
        client_ids: formData.client_ids.filter(id => id !== clientId),
        principal_client_id: formData.principal_client_id === clientId ? '' : formData.principal_client_id
      });
    } else {
      setFormData({
        ...formData,
        client_ids: [...formData.client_ids, clientId]
      });
    }
  };

  const resetForm = () => {
    setFormData({
      group_name: '',
      group_type: 'famille',
      principal_client_id: '',
      client_ids: [],
      description: ''
    });
  };

  const getGroupTypeLabel = (type) => {
    const types = {
      'famille': '👨‍👩‍👧‍👦 Famille',
      'couple': '💑 Couple',
      'groupe': '👥 Groupe'
    };
    return types[type] || type;
  };

  const getGroupTypeColor = (type) => {
    const colors = {
      'famille': 'bg-blue-100 text-blue-800',
      'couple': 'bg-pink-100 text-pink-800',
      'groupe': 'bg-purple-100 text-purple-800'
    };
    return colors[type] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="text-lg">Chargement...</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
              <Users className="h-8 w-8 text-blue-600" />
              Groupes de Clients
            </h1>
            <p className="text-gray-600 mt-1">
              Gérez les familles et groupes de clients
            </p>
          </div>

          {(user.role === 'admin' || user.role === 'subadmin') && (
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700">
                  <UserPlus className="mr-2 h-4 w-4" />
                  Créer un Groupe
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Créer un Nouveau Groupe de Clients</DialogTitle>
                </DialogHeader>

                <form onSubmit={handleSubmit} className="space-y-4">
                  {/* Group Name */}
                  <div>
                    <Label>Nom du Groupe *</Label>
                    <Input
                      value={formData.group_name}
                      onChange={(e) => setFormData({ ...formData, group_name: e.target.value })}
                      placeholder="Ex: Famille Dupont"
                      required
                    />
                  </div>

                  {/* Group Type */}
                  <div>
                    <Label>Type de Groupe *</Label>
                    <Select value={formData.group_type} onValueChange={(value) => setFormData({ ...formData, group_type: value })}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="famille">👨‍👩‍👧‍👦 Famille</SelectItem>
                        <SelectItem value="couple">💑 Couple</SelectItem>
                        <SelectItem value="groupe">👥 Groupe</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Description */}
                  <div>
                    <Label>Description (optionnel)</Label>
                    <Input
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      placeholder="Ex: Demande de résidence permanente familiale"
                    />
                  </div>

                  {/* Client Selection */}
                  <div>
                    <Label>Sélectionner les Clients * (minimum 2)</Label>
                    <div className="border rounded-lg p-4 max-h-60 overflow-y-auto mt-2 space-y-2">
                      {clients.length === 0 ? (
                        <p className="text-sm text-gray-500">Aucun client disponible (tous les clients sont déjà dans des groupes)</p>
                      ) : (
                        clients.map((client) => (
                          <div
                            key={client.id}
                            className={`flex items-center gap-3 p-3 rounded border cursor-pointer hover:bg-gray-50 ${
                              formData.client_ids.includes(client.id) ? 'bg-blue-50 border-blue-300' : 'border-gray-200'
                            }`}
                            onClick={() => handleClientSelection(client.id)}
                          >
                            <input
                              type="checkbox"
                              checked={formData.client_ids.includes(client.id)}
                              onChange={() => {}}
                              className="h-4 w-4"
                            />
                            <div className="flex-1">
                              <div className="font-medium">{client.full_name}</div>
                              <div className="text-sm text-gray-600">{client.email}</div>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                    <p className="text-sm text-gray-500 mt-1">
                      {formData.client_ids.length} client(s) sélectionné(s)
                    </p>
                  </div>

                  {/* Principal Client */}
                  {formData.client_ids.length > 0 && (
                    <div>
                      <Label>Client Principal *</Label>
                      <Select
                        value={formData.principal_client_id}
                        onValueChange={(value) => setFormData({ ...formData, principal_client_id: value })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Choisir le client principal" />
                        </SelectTrigger>
                        <SelectContent>
                          {clients
                            .filter((c) => formData.client_ids.includes(c.id))
                            .map((client) => (
                              <SelectItem key={client.id} value={client.id}>
                                {client.full_name}
                              </SelectItem>
                            ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex gap-2 pt-4">
                    <Button type="submit" className="flex-1">
                      Créer le Groupe
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setDialogOpen(false);
                        resetForm();
                      }}
                    >
                      Annuler
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>

        {/* Groups Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {groups.length === 0 ? (
            <Card className="col-span-full">
              <CardContent className="p-12 text-center text-gray-500">
                <Users className="h-16 w-16 mx-auto mb-4 opacity-30" />
                <p className="text-lg">Aucun groupe de clients</p>
                <p className="text-sm mt-2">Créez un groupe pour regrouper plusieurs clients</p>
              </CardContent>
            </Card>
          ) : (
            groups.map((group) => (
              <Card key={group.id} className="hover:shadow-lg transition-shadow">
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <CardTitle className="text-xl">{group.group_name}</CardTitle>
                      <Badge className={`mt-2 ${getGroupTypeColor(group.group_type)}`}>
                        {getGroupTypeLabel(group.group_type)}
                      </Badge>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {/* Principal Client */}
                  <div className="mb-3 p-3 bg-blue-50 rounded-lg">
                    <div className="flex items-center gap-2 text-sm font-semibold text-blue-900 mb-1">
                      <UserCheck className="h-4 w-4" />
                      Client Principal
                    </div>
                    <div className="text-sm text-blue-800">
                      {group.principal_client?.full_name}
                    </div>
                  </div>

                  {/* Members Count */}
                  <div className="text-sm text-gray-600 mb-3">
                    <strong>{group.clients?.length || 0}</strong> membre(s)
                  </div>

                  {/* Description */}
                  {group.description && (
                    <p className="text-sm text-gray-600 mb-3 italic">
                      {group.description}
                    </p>
                  )}

                  {/* Members List */}
                  <div className="space-y-2 mb-4">
                    {group.clients?.slice(0, 3).map((client) => (
                      <div key={client.id} className="text-sm flex items-center gap-2">
                        <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                        <span>{client.full_name}</span>
                      </div>
                    ))}
                    {group.clients?.length > 3 && (
                      <div className="text-sm text-gray-500 ml-4">
                        +{group.clients.length - 3} autre(s)
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2 pt-3 border-t">
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1"
                      onClick={() => navigate(`/client-groups/${group.id}`)}
                    >
                      <Eye className="h-4 w-4 mr-1" />
                      Voir
                    </Button>
                    
                    {(user.role === 'admin' || user.role === 'subadmin') && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDeleteGroup(group.id, group.group_name)}
                        className="text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </div>
    </Layout>
  );
};

export default ClientGroups;
